import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { planCategories } from 'src/lib/constants'
import { Bathrooms } from 'src/types/plan.interface'

import { USER_DASHBOARD } from '../../lib/api-routes'
import { instance } from '../../lib/axiosInstance'

export interface DashboardCardLabelAndCounts {
  count: number
  labelKey: string
}
export interface PlanStatusByCategory {
  [key: string]: DashboardCardLabelAndCounts & { percent: number }
}
export interface TotalPlanStatus extends DashboardCardLabelAndCounts {
  planStatusByCategory: PlanStatusByCategory
}

export interface RecentlyCreatedPlans {
  bathrooms: Bathrooms
  bedrooms: number
  communityNumber: string
  communityPlanId: string
  homeConditionedSpaceSqft: number
  isNew?: boolean
  plan: string
  planCategory: planCategories
  planCreatedDateTime: string
  planElevation: string
  region: string
  stories: number
}

export interface RecentDraftPlans {
  communityNumber: string
  communityPlanId: string
  plan: string
  planCategory: planCategories
  planCollaborators: { userId: string }[]
  planElevation: string
  planModifiedDateTime: string
  region: string
}
export interface DashBoardData {
  myDrafts: DashboardCardLabelAndCounts
  myPlans: DashboardCardLabelAndCounts
  recentDraftPlans: RecentDraftPlans[]
  recentSubmittedPlans: RecentlyCreatedPlans[]
  totalCommunities: DashboardCardLabelAndCounts
  totalCorePlans: DashboardCardLabelAndCounts
  totalPlans: DashboardCardLabelAndCounts
  totalPlanStatus: TotalPlanStatus
  totalValueEngineeredPlans: DashboardCardLabelAndCounts
}

export const getDashboardData = createAsyncThunk(
  'api/dashboard-data',
  async () => {
    try {
      const response = await instance.get<DashBoardData>(USER_DASHBOARD, {
        params: {
          isMultiFamily: false,
        },
      })
      return response?.data
    } catch (error) {
      throw Error('Failed to Fetch the Plans')
    }
  }
)

export const getMultiFamilyDashboardData = createAsyncThunk(
  'api/multi-family-dashboard-data',
  async () => {
    try {
      const response = await instance.get<DashBoardData>(USER_DASHBOARD, {
        params: {
          isMultiFamily: true,
        },
      })
      return response?.data
    } catch (error) {
      throw Error('Failed to Fetch the Plans')
    }
  }
)
export interface DashBoardInitialState {
  data: DashBoardData | null
  error: string | null
  loading: boolean
  multiFamilyData: DashBoardData | null
  multiFamilyError: string | null
  multiFamilyLoading: boolean
  selectedCommunityPlanId: string | null
}

const initialState: DashBoardInitialState = {
  data: {
    myDrafts: { count: 0, labelKey: '' },
    myPlans: { count: 0, labelKey: '' },
    recentDraftPlans: [],
    recentSubmittedPlans: [],
    totalCommunities: { count: 0, labelKey: '' },
    totalCorePlans: { count: 0, labelKey: '' },
    totalPlans: { count: 0, labelKey: '' },
    totalPlanStatus: {
      count: 0,
      labelKey: '',
      planStatusByCategory: {},
    },
    totalValueEngineeredPlans: { count: 0, labelKey: '' },
  },
  error: null,
  loading: false,
  multiFamilyData: {
    myDrafts: { count: 0, labelKey: '' },
    myPlans: { count: 0, labelKey: '' },
    recentDraftPlans: [],
    recentSubmittedPlans: [],
    totalCommunities: { count: 0, labelKey: '' },
    totalCorePlans: { count: 0, labelKey: '' },
    totalPlans: { count: 0, labelKey: '' },
    totalPlanStatus: {
      count: 0,
      labelKey: '',
      planStatusByCategory: {},
    },
    totalValueEngineeredPlans: { count: 0, labelKey: '' },
  },
  multiFamilyError: null,
  multiFamilyLoading: false,
  selectedCommunityPlanId: null,
}

const DashboardDataSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getDashboardData.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getDashboardData.fulfilled, (state, action) => {
        state.data = action.payload
        state.loading = false
        state.error = null
      })
      .addCase(getDashboardData.rejected, (state, action) => {
        state.data = null
        state.loading = false
        state.error = action.error.message || 'Failed to fetch Dashboard Data'
      })
      .addCase(getMultiFamilyDashboardData.pending, (state) => {
        state.multiFamilyLoading = true
        state.multiFamilyError = null
      })
      .addCase(getMultiFamilyDashboardData.fulfilled, (state, action) => {
        state.multiFamilyData = action.payload
        state.multiFamilyLoading = false
        state.error = null
      })
      .addCase(getMultiFamilyDashboardData.rejected, (state, action) => {
        state.multiFamilyData = null
        state.multiFamilyLoading = false
        state.multiFamilyError =
          action.error.message || 'Failed to fetch Dashboard Data'
      })
  },
  initialState,
  name: 'Dashboard',
  reducers: {
    // Reducer to remove an item from recentDraftPlans (Temporary change)
    removeItemFromRecentDraftPlans: (state, action: PayloadAction<string>) => {
      if (state.data?.recentDraftPlans) {
        state.data.recentDraftPlans = state.data.recentDraftPlans.filter(
          (plan) => plan.communityPlanId !== action.payload
        )
      }
    },
    setSelectedCommunityPlanId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.selectedCommunityPlanId = action.payload
    },
  },
})

export const { removeItemFromRecentDraftPlans, setSelectedCommunityPlanId } =
  DashboardDataSlice.actions

export const { reducer: dashboardDataReducer } = DashboardDataSlice
